import React from "react";

import { GovernanceBones } from "@/components/governance/GovernanceBones";
import { ReadinessBadge } from "@/components/shared/ReadinessBadge";
import { userDisplayName } from "@/components/shared/user-display-name";
import { mayReturnToDraft } from "@/components/shared/workflow-queue-actions";
import {
  Alert,
  Badge,
  Button,
  countLabel,
  FormField,
  Icon,
  Panel,
  useConfirm,
} from "@/components/ui";
import { SelectField } from "@/components/ui/Form";
import { resolveProgrammeLeadContact } from "@/export/programme-lead";
import { downloadSnapshotArtefact } from "@/export/snapshot-artefact";
import { useTenantProviderSettings } from "@/hooks/useTenantProviderSettings";
import type { EditorProgrammeMetadata, ProgrammePlatformClient, SessionContext } from "@/platform";
import { useOptionalWorkspaceContext } from "@/router/workspace-context";
import type { ExportFormat, ReadinessBlockerTarget } from "@/server/types";
import type { Programme } from "@/types";
import { withTenantProviderSettings } from "@/utils/provider-settings";

import { GovernanceApprovalRecord } from "./GovernanceApprovalRecord";
import { GovernanceJourney } from "./GovernanceJourney";
import { openRequirementCount } from "./GovernanceRequirements";
import { ReadinessOverrideDialog } from "./ReadinessOverrideDialog";
import { ReturnToDraft } from "./ReturnToDraft";
import { useProgrammeGovernance } from "./useProgrammeGovernance";

export function ProgrammeGovernancePanel({
  client,
  session,
  programme,
  onProgrammeChanged,
  onRevisionCreated,
  onRelationshipOpen = undefined,
  hasUnsavedChanges,
  hrefFor,
  nameFor = undefined,
}: {
  client: ProgrammePlatformClient;
  session: SessionContext;
  programme: EditorProgrammeMetadata;
  onProgrammeChanged: () => Promise<void>;
  onRevisionCreated: (programmeId: string) => Promise<void>;
  onRelationshipOpen?: (programmeId: string) => void;
  /** A tenant user's name, from their id; ids it cannot name are shown as they are. */
  nameFor?: (userId: string) => string | undefined;
  /** The editor address a readiness blocker is cleared at, or null for none. */
  hrefFor: (target: ReadinessBlockerTarget) => string | null;
  /**
   * Whether the editor is still holding text that has not reached the platform.
   *
   * A capture taken mid-keystroke freezes a draft nobody wrote into a record
   * that by design is never corrected, and the published-curriculum endpoint
   * projects narrative straight from a captured revision — so a missed flush
   * puts a half-typed sentence on the open web.
   *
   * Required, matching `ChangeCaseGovernancePanel`: a default of `false` means
   * a caller that forgets it silently gets no guard at all.
   */
  hasUnsavedChanges: boolean;
}): React.ReactElement {
  const confirm = useConfirm();
  const tenantProviderSettings = useTenantProviderSettings();
  const platformUsers = useOptionalWorkspaceContext()?.platformUsers ?? [];
  const [downloadFailed, setDownloadFailed] = React.useState(false);
  const displayName = (userId: string): string =>
    nameFor?.(userId) ?? userDisplayName(userId, { self: session.user });
  const model = useProgrammeGovernance({
    client,
    session,
    programme,
    onProgrammeChanged,
    onRevisionCreated,
  });
  const {
    governance,
    reviewRounds,
    loading,
    error,
    actionPending,
    overrideReason,
    setOverrideReason,
    overrideConfirmed,
    setOverrideConfirmed,
    overrideReference,
    exportFormat,
    setExportFormat,
    expanded,
    effectiveFrom,
    setEffectiveFrom,
    applicabilityRevisionId,
    publicationConsent,
    setPublicationConsent,
    runAction,
    leadSigned,
    qaSigned,
    exportRefusal,
    canCreateRevision,
    approvalCandidate,
    submittedRevision,
    approvedRevision,
    canSetApplicability,
    overrideAction,
    overrideGate,
    overrideBlockers,
    closeOverride,
    applyReadinessOverride,
    createRevision,
    addApplicability,
    selectApplicabilityRevision,
    refresh,
  } = model;
  const openCount = governance ? openRequirementCount(governance, programme.workflowState) : 0;

  // Every write here refuses the same way while another is in flight, as the
  // change-case panel beside it already did.
  const refusal = (missing?: string): string | undefined =>
    actionPending ? "Wait for the change in progress to finish." : missing;

  return (
    <Panel
      id="readiness-and-approval"
      tone="inset"
      headingLevel={2}
      className="mb-4"
      data-testid="governance-panel"
      // Named as the nav names it. The anchor keeps its old spelling, which
      // sent notifications carry and nothing can rewrite.
      title="Governance"
      description="See what must be completed and take the next permitted action."
      actions={
        governance ? (
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            {openCount > 0 ? <Badge variant="outline">{openCount} open</Badge> : null}
            <ReadinessBadge
              state={governance.readiness.state}
              data-testid="governance-readiness-state"
            />
          </div>
        ) : null
      }
    >
      {loading ? (
        <div role="status" aria-live="polite" className="mt-4">
          <span className="sr-only">Evaluating readiness…</span>
          <GovernanceBones />
        </div>
      ) : null}
      {error ? (
        <div className="mt-4">
          <Alert tone="warning">
            <span className="flex flex-wrap items-center gap-3">
              <span>{error.message}</span>
              {/* Only a failed read has nothing on screen to act from. */}
              {governance ? null : (
                <Button variant="ghost" size="sm" onClick={() => refresh()}>
                  Try again
                </Button>
              )}
            </span>
          </Alert>
        </div>
      ) : null}
      {downloadFailed ? (
        <div className="mt-4" data-testid="final-export-download-failed">
          <Alert tone="warning">
            The programme is locked, but its file could not be built. Download it from the
            submission pack.
          </Alert>
        </div>
      ) : null}
      {overrideReference ? (
        <div className="mt-4" data-testid="readiness-override-success">
          <Alert tone="success">
            Override recorded and the programme moved forward. Reference: {overrideReference}
          </Alert>
        </div>
      ) : null}

      <GovernanceJourney
        model={model}
        client={client}
        programme={programme}
        hasUnsavedChanges={hasUnsavedChanges}
        hrefFor={hrefFor}
        nameFor={displayName}
      >
        <GovernanceApprovalRecord
          governance={governance}
          programme={programme}
          reviewRounds={reviewRounds}
          expanded={expanded}
          actionPending={actionPending}
          leadSigned={leadSigned}
          qaSigned={qaSigned}
          submittedRevision={submittedRevision}
          approvedRevision={approvedRevision}
          approvalCandidate={approvalCandidate}
          canSetApplicability={canSetApplicability}
          applicabilityRevisionId={applicabilityRevisionId}
          effectiveFrom={effectiveFrom}
          onEffectiveFromChange={setEffectiveFrom}
          publicationConsent={publicationConsent}
          onPublicationConsentChange={setPublicationConsent}
          onSelectApplicabilityRevision={selectApplicabilityRevision}
          onAddApplicability={addApplicability}
          onRelationshipOpen={onRelationshipOpen}
          nameFor={displayName}
        />
      </GovernanceJourney>

      {governance && mayReturnToDraft(session, programme) ? (
        <ReturnToDraft
          workflowState={programme.workflowState}
          actionPending={actionPending}
          hasUnsavedChanges={hasUnsavedChanges}
          error={error}
          refusal={governance.returnToDraftRefusal}
          onReturn={(reason) =>
            runAction(() =>
              client.transitionWorkflowWithOverride({
                programmeId: programme.id,
                nextState: "Draft",
                note: reason,
              }),
            )
          }
        />
      ) : null}

      {governance ? (
        <>
          {programme.workflowState === "Approved" ||
          programme.workflowState === "Final Descriptor Signed Off" ? (
            <div className="mt-4 flex flex-wrap items-end gap-2">
              <FormField
                label="Final export format"
                htmlFor="final-export-format"
                className="w-full max-w-52"
              >
                <SelectField
                  id="final-export-format"
                  size="sm"
                  value={exportFormat}
                  onChange={(next) => setExportFormat(next as ExportFormat)}
                  options={[
                    { value: "descriptor", label: "Programme descriptor" },
                    { value: "schedule", label: "Programme schedule" },
                    { value: "json", label: "Governed JSON" },
                  ]}
                  data-testid="final-export-format"
                />
              </FormField>
              <Button
                size="sm"
                variant="primary"
                type="button"
                data-testid="final-export-lock"
                // One expression for both halves: refused and reasoned can
                // otherwise drift apart, which is how the in-flight window
                // came to refuse in silence.
                disabled={Boolean(refusal(exportRefusal))}
                disabledReason={refusal(exportRefusal)}
                onClick={async () => {
                  const confirmed = await confirm({
                    title: "Export and lock this programme?",
                    body: "The approved content is locked for good. Later changes need a new revision.",
                    confirmLabel: "Export and lock",
                    tone: "danger",
                    "data-testid": "final-export-confirmation",
                  });
                  if (!confirmed) {
                    return;
                  }
                  setDownloadFailed(false);
                  const locked: { data?: unknown } = {};
                  const exported = await runAction(async () => {
                    const result = await client.exportFinal({
                      programmeId: programme.id,
                      format: exportFormat,
                    });
                    locked.data = result.snapshot.data;
                  });
                  // The lock has committed by now; a file that fails to build
                  // is not a failed export, and a retry of one cannot run.
                  if (exported) {
                    await (
                      exportFormat === "descriptor"
                        ? resolveProgrammeLeadContact(client, programme.id, platformUsers)
                        : Promise.resolve(undefined)
                    )
                      .then((lead) =>
                        downloadSnapshotArtefact(
                          exportFormat,
                          withTenantProviderSettings(
                            locked.data as unknown as Programme,
                            tenantProviderSettings,
                          ),
                          lead,
                        ),
                      )
                      .catch(() => setDownloadFailed(true));
                  }
                }}
              >
                <Icon name="lock-key" aria-hidden /> Export and lock
              </Button>
            </div>
          ) : null}

          {/* No live region: the panel renders its whole text on mount, so
              the role would announce nothing new. */}
          {programme.workflowState === "Exported and Locked" ? (
            <div className="mt-4">
              <Alert tone="success" icon="lock-key">
                <div>
                  Locked with {countLabel(governance.snapshots.length, "immutable snapshot")} and{" "}
                  {countLabel(governance.exports.length, "governed export record")}.
                </div>
                {canCreateRevision && governance.snapshots.length > 0 ? (
                  <Button
                    size="sm"
                    variant="outline"
                    type="button"
                    className="mt-3"
                    data-testid="create-revision-from-snapshot"
                    disabled={actionPending}
                    disabledReason={refusal()}
                    onClick={() => createRevision()}
                  >
                    <Icon name="git-branch" aria-hidden /> Create editable revision
                  </Button>
                ) : null}
              </Alert>
            </div>
          ) : null}
        </>
      ) : null}
      <ReadinessOverrideDialog
        overrideAction={overrideAction}
        overrideGate={overrideGate}
        overrideBlockers={overrideBlockers}
        overrideReason={overrideReason}
        onOverrideReasonChange={setOverrideReason}
        overrideConfirmed={overrideConfirmed}
        onOverrideConfirmedChange={setOverrideConfirmed}
        actionPending={actionPending}
        error={error}
        programme={programme}
        onApply={applyReadinessOverride}
        onClose={closeOverride}
      />
    </Panel>
  );
}
